import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

// Procedural soft Altai wind ambience via Web Audio API (no asset, no licensing).
export default function AmbientSound() {
  const [on, setOn] = useState(false);
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);

  const build = (ctx) => {
    const bufferSize = ctx.sampleRate * 4;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.2; // brown-ish noise
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 480;
    lp.Q.value = 0.6;

    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.0;
    noise.connect(lp).connect(noiseGain);

    // slow gusts: LFO modulating filter cutoff
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain).connect(lp.frequency);

    // warm low drone
    const drone = ctx.createOscillator();
    drone.type = "sine";
    drone.frequency.value = 110;
    const droneGain = ctx.createGain();
    droneGain.gain.value = 0.0;
    drone.connect(droneGain);

    const master = ctx.createGain();
    master.gain.value = 0.0;
    noiseGain.connect(master);
    droneGain.connect(master);
    master.connect(ctx.destination);

    noise.start();
    lfo.start();
    drone.start();
    return { master, noiseGain, droneGain };
  };

  const start = async () => {
    let ctx = ctxRef.current;
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
      ctxRef.current = ctx;
      nodesRef.current = build(ctx);
    }
    try { await ctx.resume(); } catch (_) {}
    const { master, noiseGain, droneGain } = nodesRef.current;
    const t = ctx.currentTime;
    noiseGain.gain.cancelScheduledValues(t);
    noiseGain.gain.linearRampToValueAtTime(0.5, t + 2.5);
    droneGain.gain.linearRampToValueAtTime(0.06, t + 2.5);
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value, t);
    master.gain.linearRampToValueAtTime(0.16, t + 2.5);
    setOn(true);
    localStorage.setItem("argali_sound", "on");
  };

  const stop = () => {
    const ctx = ctxRef.current;
    if (!ctx || !nodesRef.current) { setOn(false); return; }
    const { master } = nodesRef.current;
    const t = ctx.currentTime;
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value, t);
    master.gain.linearRampToValueAtTime(0.0, t + 0.8);
    setOn(false);
    localStorage.setItem("argali_sound", "off");
  };

  const toggle = () => (on ? stop() : start());

  useEffect(() => {
    const enable = () => start();
    window.addEventListener("argali:enable-sound", enable);
    // resume last preference on first user gesture (autoplay policy safe)
    if (localStorage.getItem("argali_sound") === "on") {
      const once = () => { start(); window.removeEventListener("pointerdown", once); };
      window.addEventListener("pointerdown", once, { once: true });
    }
    return () => window.removeEventListener("argali:enable-sound", enable);
    // eslint-disable-next-line
  }, []);

  useEffect(() => () => { if (ctxRef.current) ctxRef.current.close(); }, []);

  return (
    <button
      data-testid="sound-toggle"
      onClick={toggle}
      aria-label={on ? "Выключить звук" : "Включить звук"}
      title={on ? "Звук включён" : "Атмосфера Алтая"}
      className="fixed bottom-5 right-5 z-[80] w-11 h-11 flex items-center justify-center border transition-colors hover:border-argali-gold"
      style={{ borderColor: "rgba(215,184,117,0.35)", background: "rgba(17,17,15,0.6)", backdropFilter: "blur(8px)" }}
    >
      {on ? <Volume2 size={18} color="#C88A2B" /> : <VolumeX size={18} color="#D7B875" />}
    </button>
  );
}
